import React, { useState, useRef } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '../ui/button';
import {
  MapPin, ClipboardList, CornerUpLeft, Copy, RefreshCw, CheckCheck, Share2,
  Plus, ImageIcon, Camera, Smile, Send, X, Eye, RotateCcw, Reply,
  Mic, ChevronDown, ChevronUp, Sparkles,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { useSession } from '../../contexts/SessionContext';
import CounterRecorderButton from './CounterRecorderButton';
import { GANAPATHI_PARKED } from '../../config/features';

// ── Local constants for chat (mirror of what SessionDetail used) ──────────
const EMOJIS = {
  'Smileys': ['😊','😀','😃','😄','😁','😆','😅','😂','🤣','😍','🥰','😘','😎','🤩','😇','🙂','😉','😋','🤔','😐','😑','😶','😏','😒','😞','😔','😟','😕','😢','😭','😤','😠','😡','😱','😰','😓','😴'],
  'Gestures': ['👍','👎','👌','✌️','🤞','🤙','👈','👉','👆','👇','☝️','👋','✋','🙏','🤝','👏','🙌','💪','💃','🤦','🤷'],
  'Business': ['💎','💍','💰','💵','💳','🎁','🛍️','👜','💼','📱','📞','📸','💬','📝','📋','📅','⏰','🔑','✅','❌','⭐','🔥','💯','❗','❓','💡','📊','📈','🚀','🎉','🏆','👑'],
};
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
const _getRId = (u) => (typeof u === 'object' ? u.id : u);

// ── Collapsible transcript card for counter-meeting recordings ────────────
const TranscriptCard = ({ msg, isMe }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [take, setTake] = React.useState(null);
  const [takeLoading, setTakeLoading] = React.useState(false);
  const transcript = msg.transcript_full || '';
  const dur = msg.duration_seconds;
  const durStr = dur
    ? (dur >= 60 ? `${Math.floor(dur / 60)}m ${Math.floor(dur % 60)}s` : `${Math.floor(dur)}s`)
    : '';
  const preview = transcript.length > 120 ? transcript.slice(0, 120).trim() + '…' : transcript;

  const loadTake = async () => {
    if (take || takeLoading) return;
    setTakeLoading(true);
    try {
      const api = (await import('../../services/api')).default;
      const r = await api.post(`/counter-recording/${msg.recording_id}/take`);
      setTake(r.data.take);
    } catch (e) {
      setTake(`(failed: ${e?.response?.data?.detail || 'try again'})`);
    } finally {
      setTakeLoading(false);
    }
  };

  return (
    <div
      className={`min-w-[220px] max-w-[320px] rounded-lg overflow-hidden ${
        isMe ? 'bg-emerald-50/70 border border-emerald-200' : 'bg-gray-50 border border-gray-200'
      }`}
    >
      <div className="flex items-center gap-2 px-2.5 py-2 border-b border-emerald-100">
        <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
          <Mic className="w-3.5 h-3.5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-gray-800 leading-tight">Counter-meeting recording</p>
          {durStr && <p className="text-[10px] text-gray-500">{durStr}</p>}
        </div>
      </div>
      {transcript ? (
        <div className="px-2.5 py-2">
          <p className={`text-xs text-gray-700 leading-relaxed whitespace-pre-wrap ${expanded ? '' : 'line-clamp-3'}`}>
            {expanded ? transcript : preview}
          </p>
          {transcript.length > 120 && (
            <button
              onClick={() => setExpanded(e => !e)}
              className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-800"
              data-testid={`transcript-toggle-${msg._id}`}
            >
              {expanded ? (
                <><ChevronUp className="w-3 h-3" /> Hide transcript</>
              ) : (
                <><ChevronDown className="w-3 h-3" /> Show full transcript</>
              )}
            </button>
          )}
          {/* Ganapathi's take (hidden when parked) */}
          {!GANAPATHI_PARKED && (
            <div className="mt-2 pt-2 border-t border-emerald-100">
              {!take && !takeLoading && (
                <button
                  onClick={loadTake}
                  className="flex items-center gap-1 text-[11px] font-semibold text-amber-700 hover:text-amber-800 active:scale-95 transition"
                  data-testid={`gp-take-btn-${msg._id}`}
                >
                  <Sparkles className="w-3 h-3" /> 🙏 Ganapathi&apos;s take
                </button>
              )}
              {takeLoading && (
                <p className="text-[11px] text-amber-700 italic flex items-center gap-1">
                  <Sparkles className="w-3 h-3 animate-pulse" /> Ganapathi is reading…
                </p>
              )}
              {take && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-2" data-testid={`gp-take-body-${msg._id}`}>
                  <p className="text-[10px] font-semibold text-amber-800 uppercase tracking-wide mb-0.5 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Ganapathi&apos;s take
                  </p>
                  <p className="text-xs text-amber-900 leading-relaxed">{take}</p>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="px-2.5 py-2 text-xs text-gray-400 italic">No speech detected.</div>
      )}
    </div>
  );
};


const ChatTab = () => {
  const { currentUser, isAdvisor } = useAuth();
  const { toast } = useToast();
  const {
    session, chatItems, messages,
    chatContainerRef, messagesEndRef, inputRef, imageInputRef, cameraInputRef, pressTimerRef,
    activeContextMsg, setActiveContextMsg,
    replyingTo, setReplyingTo,
    reactionTooltip, setReactionTooltip, handleReact,
    unreadMentions, jumpToNextMention,
    canReopenSession, handleReopenSession,
    showTagMenu, setShowTagMenu, tagFilter, setTagFilter,
    leads, taggablePeople,
    newMessage, setNewMessage,
    handleImageUpload, uploadingImage,
    showAttachMenu, setShowAttachMenu,
    showEmojiPicker, setShowEmojiPicker,
    handleSendMessage,
  } = useSession();

  // ── Swipe-to-reply state (per-bubble, one at a time) ──────────────────────
  // On touchstart on a message bubble: record startX/Y and start long-press.
  // On touchmove: if horizontal drift > 8px, cancel long-press; if dx > 0 update translate.
  // On touchend: if dx > 50px → open reply mode; snap back either way.
  const [swipeReply, setSwipeReply] = useState({ msgId: null, dx: 0 });
  const swipeRef = useRef({ startX: 0, startY: 0, active: false, msgId: null, dx: 0 });
  const SWIPE_REPLY_THRESHOLD = 50;
  const SWIPE_REPLY_MAX = 90;

  return (
    <>
      {/* Chat Messages */}
      <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 bg-[#e5ddd5] space-y-2 relative" data-testid="chat-messages">
        {chatItems.map((item, idx) => {
          if (item.type === 'visit') {
            const v = item.data;
            return (
              <div key={`visit-${v.visit_number}`} className="my-3" data-testid={`visit-divider-${v.visit_number}`}>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-px bg-emerald-300" />
                  <div className="flex items-center gap-1.5 bg-emerald-700 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-sm flex-shrink-0">
                    <MapPin className="w-3 h-3" />
                    <span>Visit #{v.visit_number}</span>
                    <span className="opacity-75">·</span>
                    <span>{new Date(v.date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    <span className="opacity-75">·</span>
                    <span>{v.salesman_name}</span>
                  </div>
                  <div className="flex-1 h-px bg-emerald-300" />
                </div>
                {v.summary && (
                  <p className="text-center text-[11px] text-emerald-700 italic mt-1 px-4">{v.summary}</p>
                )}
              </div>
            );
          }
          const msg = item.data;
          const isMe = msg.sender_id === currentUser?.id;
          const isSystemNote = msg.type === 'system_note';
          const isSystem = msg.type === 'system';
          if (isSystemNote) return (
            <div key={msg._id} id={`msg-${msg._id}`} className="flex justify-center my-3 px-4">
              <div className="bg-teal-50 border border-teal-200 rounded-2xl px-4 py-3 max-w-[85%] text-center shadow-sm">
                <div className="flex items-center justify-center gap-1.5 text-teal-600 text-[10px] font-bold uppercase tracking-wide mb-1.5">
                  <ClipboardList className="w-3 h-3" />
                  Session Note
                </div>
                <p className="text-teal-900 text-xs leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                <p className="text-teal-400 text-[9px] mt-1.5">— {msg.sender_name}</p>
              </div>
            </div>
          );
          if (isSystem) {
            const timestamp = msg.timestamp ? new Date(msg.timestamp) : null;
            const timeStr = timestamp ? timestamp.toLocaleString('en-IN', {
              day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
            }) : '';
            return (
              <div key={msg._id} id={`msg-${msg._id}`} className="flex justify-center my-2">
                <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 rounded-2xl max-w-[85%] text-center">
                  <p className="leading-relaxed">{msg.content}</p>
                  {timeStr && (<p className="text-amber-500 text-[9px] mt-1">{timeStr}</p>)}
                </div>
              </div>
            );
          }

          const isActive = activeContextMsg?._id === msg._id;
          const isSwipeTarget = swipeReply.msgId === msg._id;
          const swipeDx = isSwipeTarget ? swipeReply.dx : 0;
          const showSwipeArrow = swipeDx > 8;
          const swipeReady = swipeDx >= SWIPE_REPLY_THRESHOLD;

          const handleTouchStart = (e) => {
            const t = e.touches[0];
            swipeRef.current = { startX: t.clientX, startY: t.clientY, active: false, msgId: msg._id, dx: 0 };
            pressTimerRef.current = setTimeout(() => setActiveContextMsg(msg), 500);
          };
          const handleTouchMove = (e) => {
            const t = e.touches[0];
            if (!t) return; // multi-touch pinch guard
            const dx = t.clientX - swipeRef.current.startX;
            const dy = t.clientY - swipeRef.current.startY;
            // Any real movement cancels the long-press
            if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
              clearTimeout(pressTimerRef.current);
            }
            // Vertical scroll intent → don't hijack for swipe-reply
            if (!swipeRef.current.active && Math.abs(dy) > Math.abs(dx) * 0.8) return;
            // Only right-swipes register for reply
            if (dx > 6 || swipeRef.current.active) {
              swipeRef.current.active = true;
              const clamped = Math.max(0, Math.min(dx, SWIPE_REPLY_MAX));
              swipeRef.current.dx = clamped;
              setSwipeReply({ msgId: msg._id, dx: clamped });
            }
          };
          const handleTouchEnd = () => {
            clearTimeout(pressTimerRef.current);
            const dx = swipeRef.current.dx;
            if (swipeRef.current.active && dx >= SWIPE_REPLY_THRESHOLD) {
              setReplyingTo(msg);
              setActiveContextMsg(null);
              // Haptic (Android/some iOS) — quiet ping for premium feel
              if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(8);
              // Focus input on next tick so mobile keyboard opens after gesture
              setTimeout(() => inputRef.current?.focus(), 30);
            }
            swipeRef.current = { startX: 0, startY: 0, active: false, msgId: null, dx: 0 };
            setSwipeReply({ msgId: null, dx: 0 });
          };

          const handleCopy = async () => {
            if (msg.type === 'image') {
              try {
                const response = await fetch(msg.content);
                const blob = await response.blob();
                const mimeType = blob.type || 'image/png';
                await navigator.clipboard.write([new ClipboardItem({ [mimeType]: blob })]);
                toast({ title: 'Image copied!', duration: 1200 });
              } catch {
                navigator.clipboard.writeText(msg.content);
                toast({ title: 'Image URL copied!', duration: 1200 });
              }
            } else {
              try {
                await navigator.clipboard.writeText(msg.content);
                toast({ title: 'Copied!', duration: 1200 });
              } catch {
                toast({ title: 'Copy not supported on this device', duration: 1500 });
              }
            }
            setActiveContextMsg(null);
          };

          const handleShare = async () => {
            try {
              if (msg.type === 'image') {
                const response = await fetch(msg.content);
                const blob = await response.blob();
                const file = new File([blob], 'shared-image.jpg', { type: blob.type || 'image/jpeg' });
                if (navigator.canShare && navigator.canShare({ files: [file] })) {
                  await navigator.share({ files: [file], title: 'DiaForge' });
                } else {
                  await navigator.share({ url: msg.content, title: 'DiaForge' });
                }
              } else {
                await navigator.share({ text: msg.content, title: 'DiaForge' });
              }
            } catch (e) {
              if (e.name !== 'AbortError') {
                toast({ title: 'Sharing not supported on this device', duration: 1500 });
              }
            }
            setActiveContextMsg(null);
          };

          const handleReply = () => {
            setReplyingTo(msg);
            setActiveContextMsg(null);
            inputRef.current?.focus();
          };

          return (
            <div
              key={msg._id}
              id={`msg-${msg._id}`}
              className={`flex ${isMe ? 'justify-end' : 'justify-start'} group ${isActive ? 'relative z-50' : ''}`}
              onClick={() => activeContextMsg && setActiveContextMsg(null)}
            >
              <div className="flex flex-col max-w-[75%]">
                {!isActive && (
                  <div className={`flex ${isMe ? 'justify-end' : 'justify-start'} gap-1 mb-0.5 opacity-0 group-hover:opacity-100 transition-opacity`}>
                    <button onClick={handleReply} className="p-1 rounded-full bg-white shadow-sm hover:bg-emerald-50 text-gray-500 hover:text-emerald-600" title="Reply">
                      <CornerUpLeft className="w-3.5 h-3.5" />
                    </button>
                    {msg.type !== 'image' && (
                      <button onClick={handleCopy} className="p-1 rounded-full bg-white shadow-sm hover:bg-gray-50 text-gray-500 hover:text-gray-700" title="Copy">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
                {isActive && (
                  <div className={`flex items-center bg-white rounded-full px-2.5 py-1.5 shadow-xl border border-gray-100 mb-1.5 gap-0.5 ${isMe ? 'self-end' : 'self-start'}`}>
                    {QUICK_REACTIONS.map(emoji => (
                      <button
                        key={emoji}
                        onClick={(e) => { e.stopPropagation(); handleReact(msg._id, emoji); setActiveContextMsg(null); }}
                        className="text-[22px] hover:scale-125 active:scale-110 transition-transform leading-none px-0.5"
                        data-testid={`react-emoji-${emoji}`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
                <div
                  className={`relative rounded-lg px-3 py-2 ${isMe ? 'bg-[#dcf8c6]' : 'bg-white'} shadow-sm ${isActive ? 'ring-2 ring-emerald-400' : ''}`}
                  style={{
                    WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none',
                    transform: `translateX(${swipeDx}px)`,
                    transition: isSwipeTarget && swipeRef.current.active ? 'none' : 'transform 0.18s ease-out',
                    touchAction: 'pan-y',
                  }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onTouchCancel={handleTouchEnd}
                >
                  {/* Swipe-to-reply arrow — fades in on the left as user drags right */}
                  {showSwipeArrow && (
                    <div
                      data-testid={`swipe-reply-arrow-${msg._id}`}
                      className={`absolute top-1/2 -translate-y-1/2 -left-9 w-8 h-8 rounded-full flex items-center justify-center pointer-events-none transition-all ${
                        swipeReady ? 'bg-emerald-500 text-white scale-110' : 'bg-emerald-100 text-emerald-500 scale-95'
                      }`}
                      style={{ opacity: Math.min(1, swipeDx / SWIPE_REPLY_THRESHOLD) }}
                    >
                      <Reply className="w-4 h-4" />
                    </div>
                  )}
                  {!isMe && <p className="text-xs font-bold text-emerald-700 mb-0.5">{msg.sender_name}</p>}
                  {msg.reply_to && (
                    <div className={`border-l-4 border-emerald-500 pl-2 py-0.5 mb-1.5 rounded-r ${isMe ? 'bg-emerald-50' : 'bg-gray-50'}`}>
                      <p className="text-[10px] font-bold text-emerald-700">{msg.reply_to.sender_name}</p>
                      <p className="text-[11px] text-gray-600 truncate">{msg.reply_to.content_preview}</p>
                    </div>
                  )}
                  {msg.type === 'image' ? (
                    <div className="relative">
                      <img
                        src={msg.content}
                        alt="Shared"
                        className="rounded-lg max-w-full max-h-60 cursor-pointer"
                        onClick={() => !msg.pending && window.open(msg.content, '_blank')}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                        }}
                      />
                      <div className="hidden items-center justify-center w-40 h-28 rounded-lg bg-gray-100 text-gray-400 text-xs flex-col gap-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 3l18 18"/>
                        </svg>
                        <span>Image unavailable</span>
                      </div>
                      {msg.pending && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-lg">
                          <RefreshCw className="w-5 h-5 text-white animate-spin" />
                        </div>
                      )}
                    </div>
                  ) : msg.type === 'counter_recording' ? (
                    <TranscriptCard msg={msg} isMe={isMe} />
                  ) : (
                    <p className="text-sm text-gray-900">
                      {msg.content.split(/(@#\d+\s\S+|@[A-Za-z][A-Za-z0-9_ ]*[A-Za-z0-9])/g).map((part, i) =>
                        part.match(/^@#\d+\s/) ? (
                          <span key={i} className="font-bold text-emerald-700 bg-emerald-50 px-1 rounded">{part}</span>
                        ) : part.match(/^@[A-Za-z]/) ? (
                          <span key={i} className="font-bold text-blue-600 bg-blue-50 px-1 rounded">{part}</span>
                        ) : part
                      )}
                    </p>
                  )}
                  <div className="flex items-center justify-end gap-1 mt-0.5">
                    <span className="text-[10px] text-gray-400">
                      {msg.pending
                        ? <span className="italic text-gray-300">Sending...</span>
                        : (() => { try { return formatDistanceToNow(new Date(msg.timestamp + 'Z'), { addSuffix: true }); } catch { return ''; } })()
                      }
                    </span>
                    {isMe && !msg.pending && (
                      <CheckCheck
                        className={`w-3.5 h-3.5 flex-shrink-0 ${
                          (msg.read_by || []).filter(id => id !== msg.sender_id).length > 0
                            ? 'text-[#53bdeb]'
                            : 'text-gray-400'
                        }`}
                        data-testid={`read-tick-${msg._id}`}
                      />
                    )}
                  </div>
                </div>

                {msg.reactions && msg.reactions.filter(r => r.users && r.users.length > 0).length > 0 && (
                  <div className={`flex flex-wrap gap-1 mt-0.5 ${isMe ? 'justify-end' : 'justify-start'}`}>
                    {msg.reactions.filter(r => r.users && r.users.length > 0).map(r => {
                      const reacted = r.users.some(u => _getRId(u) === currentUser?.id);
                      return (
                        <button
                          key={r.emoji}
                          onClick={() => setReactionTooltip({ msgId: msg._id, emoji: r.emoji, users: r.users })}
                          className={`flex items-center gap-0.5 text-sm px-1.5 py-0.5 rounded-full border transition-colors active:scale-95 ${
                            reacted
                              ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                              : 'bg-white border-gray-200 text-gray-600 hover:border-emerald-200'
                          }`}
                          data-testid={`reaction-chip-${msg._id}-${r.emoji}`}
                        >
                          <span>{r.emoji}</span>
                          {r.users.length > 1 && <span className="text-xs font-semibold">{r.users.length}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}

                {isActive && (
                  <div className={`flex gap-2 mt-1.5 flex-wrap ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleReply(); }}
                      className="flex items-center gap-1.5 text-xs bg-white rounded-full px-3 py-1.5 shadow-md text-emerald-700 font-medium border border-gray-100 active:scale-95 transition-transform"
                      data-testid="context-menu-reply"
                    >
                      <CornerUpLeft className="w-3.5 h-3.5" /> Reply
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleCopy(); }}
                      className="flex items-center gap-1.5 text-xs bg-white rounded-full px-3 py-1.5 shadow-md text-gray-700 font-medium border border-gray-100 active:scale-95 transition-transform"
                      data-testid="context-menu-copy"
                    >
                      <Copy className="w-3.5 h-3.5" /> Copy
                    </button>
                    {navigator.share && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleShare(); }}
                        className="flex items-center gap-1.5 text-xs bg-white rounded-full px-3 py-1.5 shadow-md text-blue-600 font-medium border border-gray-100 active:scale-95 transition-transform"
                        data-testid="context-menu-share"
                      >
                        <Share2 className="w-3.5 h-3.5" /> Share
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />

        {unreadMentions.length > 0 && (
          <button
            onClick={jumpToNextMention}
            data-testid="mention-jump-badge"
            className="sticky bottom-3 ml-auto mr-1 flex items-center gap-1 bg-[#075E54] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg z-20 hover:bg-[#128C7E] transition-colors"
          >
            <span className="text-sm">@</span>
            <span className="text-base">↓</span>
            <span className="bg-white text-[#075E54] rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-black">{unreadMentions.length}</span>
          </button>
        )}
      </div>

      {/* Re-open Session Banner — visible only for lost sessions to admin/stock_supervisor */}
      {session.status === 'lost' && canReopenSession && (
        <div className="flex-shrink-0 mx-3 mb-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-center justify-between gap-3" data-testid="reopen-session-banner">
          <div className="flex items-center gap-2 min-w-0">
            <RotateCcw className="w-4 h-4 text-red-500 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-red-700 leading-tight">This session is marked as Lost</p>
              <p className="text-[10px] text-red-400 leading-tight truncate">Re-open to continue conversation and restore leads</p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={handleReopenSession}
            className="flex-shrink-0 bg-red-500 hover:bg-red-600 text-white text-xs px-3 h-8"
            data-testid="reopen-session-banner-btn"
          >
            Re-open Session
          </Button>
        </div>
      )}

      {/* Message Input */}
      {session.status === 'open' && !isAdvisor && (
        <div className="flex-shrink-0 bg-white border-t relative" data-testid="message-input-area">
          {showTagMenu && (
            <div className="absolute bottom-full left-0 right-0 bg-white border-t shadow-lg max-h-48 overflow-y-auto z-20" data-testid="tag-menu">
              {leads.filter(l => !tagFilter || l.product_name?.toLowerCase().includes(tagFilter.toLowerCase())).length > 0 && (
                <p className="px-4 py-1 text-[10px] font-bold text-gray-400 uppercase bg-gray-50">Leads</p>
              )}
              {leads.filter(l => !tagFilter || l.product_name?.toLowerCase().includes(tagFilter.toLowerCase())).map(lead => (
                <button
                  key={lead._id}
                  className="w-full text-left px-4 py-2 hover:bg-emerald-50 text-sm border-b flex items-center gap-2"
                  onClick={() => {
                    const tag = `@#${lead.lead_number || '?'} ${lead.product_name}`;
                    const atIndex = newMessage.lastIndexOf('@');
                    const before = atIndex >= 0 ? newMessage.substring(0, atIndex) : newMessage;
                    setNewMessage(before + tag + ' ');
                    setShowTagMenu(false);
                    setTagFilter('');
                  }}
                >
                  <span className="font-bold text-emerald-700">#{lead.lead_number}</span>
                  <span>{lead.product_name}</span>
                  {lead.main_group && <span className="text-xs text-gray-400">{lead.main_group}</span>}
                </button>
              ))}
              {taggablePeople.filter(p => !tagFilter || p.name?.toLowerCase().includes(tagFilter.toLowerCase())).length > 0 && (
                <p className="px-4 py-1 text-[10px] font-bold text-gray-400 uppercase bg-gray-50">People</p>
              )}
              {taggablePeople.filter(p => !tagFilter || p.name?.toLowerCase().includes(tagFilter.toLowerCase())).map(person => (
                <button
                  key={person.id}
                  className="w-full text-left px-4 py-2 hover:bg-blue-50 text-sm border-b flex items-center gap-2"
                  onClick={() => {
                    const tag = `@${person.name}`;
                    const atIndex = newMessage.lastIndexOf('@');
                    const before = atIndex >= 0 ? newMessage.substring(0, atIndex) : newMessage;
                    setNewMessage(before + tag + ' ');
                    setShowTagMenu(false);
                    setTagFilter('');
                  }}
                >
                  <span className="font-bold text-blue-600">{person.name}</span>
                  <span className="text-[10px] text-gray-400">{person.role}</span>
                </button>
              ))}
            </div>
          )}
          <div className="app-input-bar flex gap-3" data-testid="message-input-row">
            <input type="file" ref={imageInputRef} accept="image/*" onChange={handleImageUpload} className="hidden" />
            <input type="file" ref={cameraInputRef} accept="image/*" capture="environment" onChange={handleImageUpload} className="hidden" />

            <div className="relative flex-shrink-0">
              <Button
                size="icon" variant="ghost"
                onClick={() => setShowAttachMenu(p => !p)}
                className="text-gray-500 hover:text-emerald-600 flex-shrink-0"
                data-testid="attach-menu-btn"
                title="Attach"
              >
                <Plus className={`w-5 h-5 transition-transform ${showAttachMenu ? 'rotate-45' : ''}`} />
              </Button>
              {showAttachMenu && (
                <div className="absolute bottom-full left-0 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 p-2 flex gap-2 z-30" data-testid="attach-menu">
                  <Button
                    size="icon" variant="ghost"
                    onClick={() => { imageInputRef.current?.click(); setShowAttachMenu(false); }}
                    disabled={uploadingImage}
                    className="text-gray-500 hover:text-emerald-600 hover:bg-emerald-50"
                    data-testid="image-upload-btn"
                    title="Upload from gallery"
                  >
                    <ImageIcon className="w-5 h-5" />
                  </Button>
                  <Button
                    size="icon" variant="ghost"
                    onClick={() => { cameraInputRef.current?.click(); setShowAttachMenu(false); }}
                    disabled={uploadingImage}
                    className="text-gray-500 hover:text-emerald-600 hover:bg-emerald-50"
                    data-testid="camera-capture-btn"
                    title="Take photo"
                  >
                    {uploadingImage ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                  </Button>
                  <Button
                    size="icon" variant="ghost"
                    onClick={() => { setShowEmojiPicker(p => !p); setShowAttachMenu(false); }}
                    className="text-gray-500 hover:text-emerald-600 hover:bg-emerald-50"
                    data-testid="emoji-btn"
                    title="Emoji"
                  >
                    <Smile className="w-5 h-5" />
                  </Button>
                </div>
              )}
            </div>

            {!GANAPATHI_PARKED && (
              <div className="flex-shrink-0">
                <CounterRecorderButton sessionId={session?._id} />
              </div>
            )}

            <textarea
              ref={inputRef}
              value={newMessage}
              rows={1}
              onChange={e => {
                const val = e.target.value;
                setNewMessage(val);
                e.target.style.height = '1px';
                const newHeight = Math.min(e.target.scrollHeight, 160);
                e.target.style.height = newHeight + 'px';
                e.target.style.overflowY = e.target.scrollHeight > 160 ? 'auto' : 'hidden';
                const lastAt = val.lastIndexOf('@');
                if (lastAt >= 0 && lastAt === val.length - 1) {
                  setShowTagMenu(true); setTagFilter('');
                } else if (lastAt >= 0 && !val.substring(lastAt).includes(' ')) {
                  setShowTagMenu(true); setTagFilter(val.substring(lastAt + 1));
                } else {
                  setShowTagMenu(false);
                }
              }}
              onKeyDown={e => {
                if (e.key === 'Escape') { setShowTagMenu(false); setShowEmojiPicker(false); setReplyingTo(null); setShowAttachMenu(false); }
              }}
              placeholder="Type a message... @ to tag"
              className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2.5 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 leading-relaxed"
              style={{
                minHeight: '42px',
                maxHeight: '160px',
                overflowY: 'hidden',
                wordBreak: 'break-word',
                WebkitOverflowScrolling: 'touch',
              }}
              data-testid="message-input"
            />
            <Button size="icon" onClick={handleSendMessage} className="bg-[#075E54] hover:bg-[#064E46]" data-testid="send-btn">
              <Send className="w-4 h-4" />
            </Button>
          </div>

          {showEmojiPicker && (
            <div className="absolute bottom-full left-0 right-0 bg-white border-t shadow-lg z-20 max-h-52 overflow-y-auto" data-testid="emoji-picker">
              {Object.entries(EMOJIS).map(([cat, emojis]) => (
                <div key={cat} className="px-3 pt-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">{cat}</p>
                  <div className="flex flex-wrap gap-0.5 pb-2">
                    {emojis.map(e => (
                      <button
                        key={e}
                        onClick={() => { setNewMessage(m => m + e); inputRef.current?.focus(); }}
                        className="text-xl hover:bg-gray-100 rounded p-0.5 active:scale-110 transition-transform"
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {replyingTo && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border-t border-emerald-100" data-testid="reply-strip">
              <CornerUpLeft className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold text-emerald-700">{replyingTo.sender_name}</p>
                <p className="text-xs text-gray-600 truncate">
                  {replyingTo.type === 'image' ? '📷 Image' : replyingTo.content.substring(0, 60)}
                </p>
              </div>
              <button onClick={() => setReplyingTo(null)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {session.status === 'open' && isAdvisor && (
        <div className="flex-shrink-0 bg-gray-50 border-t px-4 py-3 flex items-center justify-center gap-2 text-gray-400" data-testid="advisor-readonly-notice">
          <Eye className="w-4 h-4" />
          <span className="text-sm">Read-only view — advisors cannot send messages</span>
        </div>
      )}
    </>
  );
};

export default ChatTab;
